import { Connection, PublicKey } from "@solana/web3.js";
import { ISolanaConfiguration } from "../config/solana.types";
import { ABaseClientService } from "./baseClientService";

import { RedemptionClient } from "./redemption/redemptionClient";
import { IDL } from "./redemption/redemption";

import { AnchorProvider, Program, Wallet } from "@coral-xyz/anchor";
import { WalletAdapter } from "../adapters/WalletAdapter";
import { findATAAddrSync } from "@uxd-protocol/uxd-client";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  Token,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";

export class RedemptionClientService extends ABaseClientService {
  private readonly client: RedemptionClient;

  constructor(
    config: ISolanaConfiguration,
    connection: Connection,
    redemptionClient: RedemptionClient
  ) {
    super(config, connection);

    this.client = redemptionClient;
  }

  public static async initialize(
    config: ISolanaConfiguration,
    connection: Connection
  ) {
    const programId = new PublicKey(config.getPrograms().redemption.address);

    const redemptionClient = await RedemptionClient.initialize(
      programId,
      connection
    );
    return new RedemptionClientService(config, connection, redemptionClient);
  }

  public setProgramWithWallet = async (wallet: Wallet) => {
    const programId = new PublicKey(
      this.config.getPrograms().redemption.address
    );

    const program = new Program(
      IDL,
      programId,
      new AnchorProvider(
        this.connection,
        wallet,
        AnchorProvider.defaultOptions()
      )
    );
    this.client.setProgram(program);
  };

  public getState() {
    return this.client.state;
  }

  public async refreshState() {
    return this.client.refreshProgramState();
  }

  public async refreshUserState(walletAdapter: WalletAdapter) {
    if (!walletAdapter?.adapter.publicKey || !this.client.getProgram()) {
      return undefined;
    }

    try {
      const claimAccountState = await this.client.refreshClaimAccountState();
      return claimAccountState;
    } catch (err) {
      console.error("Failed to refresh user state: ", err);
      return undefined;
    }
  }

  public async redeemUXPForUCT(
    uxpAmount: number,
    walletAdapter: WalletAdapter
  ) {
    const walletKey = walletAdapter.adapter.publicKey;
    if (!walletKey) {
      const err = new Error("walletAdapter have no publicKey.");
      throw err;
    }
    const instructions = [];
    const [userUctAta] = findATAAddrSync(walletKey, this.client.state.uctMint);
    const userUctAtaInfo = await this.connection.getAccountInfo(userUctAta);
    if (!userUctAtaInfo || !userUctAtaInfo.lamports) {
      const ix = Token.createAssociatedTokenAccountInstruction(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        this.client.state.uctMint,
        userUctAta,
        walletKey,
        walletKey
      );
      instructions.push(ix);
    }

    const [userUsdcAta] = findATAAddrSync(
      walletKey,
      this.client.state.usdcMint
    );
    const userUsdcAtaInfo = await this.connection.getAccountInfo(userUsdcAta);
    if (!userUsdcAtaInfo || !userUsdcAtaInfo.lamports) {
      const ix = Token.createAssociatedTokenAccountInstruction(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        this.client.state.usdcMint,
        userUsdcAta,
        walletKey,
        walletKey
      );
      instructions.push(ix);
    }

    const convertIx = await this.client.convertUxpToUct({
      uxpAmount,
      walletKey,
    });
    instructions.push(convertIx);

    const { transaction, blockhash, lastValidBlockHeight } =
      await this.makeTransaction(instructions, walletKey, 800_000);
    const signature = await this.sendTransaction(
      transaction,
      walletAdapter.adapter
    );
    await this.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    });
    return signature;
  }

  public async burnUCTForUSDC(uctAmount: number, walletAdapter: WalletAdapter) {
    const walletKey = walletAdapter.adapter.publicKey;
    if (!walletKey) {
      const err = new Error("walletAdapter have no publicKey.");
      throw err;
    }
    const instructions = [];

    const [userUsdcAta] = findATAAddrSync(
      walletKey,
      this.client.state.usdcMint
    );
    const userUsdcAtaInfo = await this.connection.getAccountInfo(userUsdcAta);
    if (!userUsdcAtaInfo || !userUsdcAtaInfo.lamports) {
      const ix = Token.createAssociatedTokenAccountInstruction(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        this.client.state.uctMint,
        userUsdcAta,
        walletKey,
        walletKey
      );
      instructions.push(ix);
    }

    const convertIx = await this.client.redeemPhaseOne({
      amount: uctAmount,
      walletKey,
    });
    instructions.push(convertIx);

    const { transaction, blockhash, lastValidBlockHeight } =
      await this.makeTransaction(instructions, walletKey, 800_000);
    const signature = await this.sendTransaction(
      transaction,
      walletAdapter.adapter
    );
    await this.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    });
    return signature;
  }

  public async claimIlliquidUSDC(
    amountUi: number,
    walletAdapter: WalletAdapter
  ) {
    const walletKey = walletAdapter.adapter.publicKey;
    if (!walletKey) {
      const err = new Error("walletAdapter have no publicKey.");
      throw err;
    }

    const instructions = [];

    const [userUsdcAta] = findATAAddrSync(
      walletKey,
      this.client.state.usdcMint
    );
    const userUsdcAtaInfo = await this.connection.getAccountInfo(userUsdcAta);
    if (!userUsdcAtaInfo || !userUsdcAtaInfo.lamports) {
      const ix = Token.createAssociatedTokenAccountInstruction(
        ASSOCIATED_TOKEN_PROGRAM_ID,
        TOKEN_PROGRAM_ID,
        this.client.state.usdcMint,
        userUsdcAta,
        walletKey,
        walletKey
      );
      instructions.push(ix);
    }

    const convertIx = await this.client.redeemPhaseTwo(amountUi, walletKey);
    instructions.push(convertIx);

    const { transaction, blockhash, lastValidBlockHeight } =
      await this.makeTransaction(instructions, walletKey, 800_000);
    const signature = await this.sendTransaction(
      transaction,
      walletAdapter.adapter
    );
    await this.confirmTransaction({
      signature,
      blockhash,
      lastValidBlockHeight,
    });
    return signature;
  }
}
